import React, { FC } from 'react';
import { PaylerModal } from '@payler/ui-components';
import {
  atom,
  useRecoilCallback,
  useRecoilValue,
} from 'recoil';
import { createRecoilKey } from '../helpers/createKey';
import { TBulkRefund } from '../../../../api/src';
import {
  Box,
  Button,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { WarningBigIcon } from '@payler/ui-icons';
import { TextStyles } from '@payler/ui-theme';
import { useCurrencyCallbacks } from '../hooks/currencies-info';
import {
  useBulkRefundActionsMutation,
  useResetBulkRefundsQuery,
} from '../hooks/payouts/queries';
import { useBulkPayoutErrorContext } from '../context/BulkPayoutErrorContext';
import ReactNumberFormat from 'react-number-format';
import { useToasts } from '../hooks/use-toasts';

type TBulkRefundConfirmationModalOpenedAtom = TBulkRefund & {
  fileName: string;
  bulkRefundId: string;
  password: string;
};

const bulkRefundConfirmationModalOpenedAtom =
  atom<TBulkRefundConfirmationModalOpenedAtom | null>({
    key: createRecoilKey('bulkRefundConfirmationModalOpenedAtom'),
    default: null,
  });

export const useShowBulkRefundConfirmationModal = () =>
  useRecoilCallback(
    ({ set }) =>
      (data: TBulkRefundConfirmationModalOpenedAtom) => {
        set(bulkRefundConfirmationModalOpenedAtom, data);
      },
    [],
  );

export const useCloseBulkRefundConfirmationModal = () =>
  useRecoilCallback(
    ({ set }) =>
      () => {
        set(bulkRefundConfirmationModalOpenedAtom, null);
      },
    [],
  );

export const BulkRefundConfirmationModal: FC<{
  onClose: () => void;
  onConfirm: () => void;
}> = ({ onClose, onConfirm }) => {
  const data = useRecoilValue(bulkRefundConfirmationModalOpenedAtom);
  const close = useCloseBulkRefundConfirmationModal();

  const handleClose = () => {
    close();
    onClose();
  };

  return (
    <PaylerModal close={handleClose} open={!!data}>
      <ModalContent onConfirm={onConfirm} onClose={handleClose} />
    </PaylerModal>
  );
};

const ModalContent: FC<{
  onConfirm: () => void;
  onClose: () => void;
}> = ({ onConfirm, onClose }) => {
  const data = useRecoilValue(bulkRefundConfirmationModalOpenedAtom);
  const { t } = useTranslation(['returns']);
  const { getCurrencySymbol, getCurrencyUnitRate } = useCurrencyCallbacks();
  const { cancelMutation, startMutation } = useBulkRefundActionsMutation();
  const { setBulkPayoutError } = useBulkPayoutErrorContext();
  const resetBulkRefunds = useResetBulkRefundsQuery();
  const toast = useToasts();
  if (!data) return null;

  const amount = `${data.amountTotal ?? 0 / getCurrencyUnitRate(data.currency)} ${getCurrencySymbol(data.currency)}`;
  const amountValidated = `${data.amountValidated ?? 0 / getCurrencyUnitRate(data.currency)} ${getCurrencySymbol(data.currency)}`;

  const handleCancelButtonClick = () => {
    cancelMutation.mutate(data.bulkRefundId, {
      onSuccess: () => {
        resetBulkRefunds();
      },
      onError: (err) => {
        setBulkPayoutError(err);
      },
      onSettled: onClose,
    });
  };
  const handleAcceptButtonClick = () => {
    startMutation.mutate(
      { password: data.password, bulkRefundId: data.bulkRefundId },
      {
        onSuccess: () => {
          resetBulkRefunds();
          onConfirm();
          toast.success(t('returns:modal.bulkRefundTransitionStarted'));
        },
        onError: (err) => {
          setBulkPayoutError(err);
        },
        onSettled: onClose,
      },
    );
  };

  return (
    <VStack>
      <VStack w="full" justifyContent="center">
        <Icon as={WarningBigIcon} w={8} h={8} />
        <Text as="h1" textStyle={TextStyles.h1} w="full" textAlign="center">
          {t('returns:modal.modalTitle')}
        </Text>
      </VStack>
      <Box textAlign="center">
        <Trans
          i18nKey={'returns:modal.modalDescription'}
          ns={['returns']}
          values={{
            fileName: data.fileName,
            rowsTotal: data.rowsTotal,
            amount,
            currency: data.currency,
          }}
          components={{
            darker: (
              <Text
                as="span"
                color="primary.500"
                textStyle={TextStyles.BodyText16Medium}
              />
            ),
            amount: (
              <Amount
                amount={data.amountTotal ?? 0}
                currency={data.currency ?? ''}
              />
            ),
          }}
        />

        {data.rowsValidated &&
          data.amountValidated &&
          (data.rowsValidated !== data.rowsTotal ||
            data.amountValidated !== data.amountTotal) && (
            <Trans
              i18nKey="returns:modal.modalDescriptionWithValidation"
              ns={['returns']}
              values={{
                rowsValidated: data.rowsValidated,
                amountValidated,
                currency: data.currency,
              }}
              components={{
                darker: (
                  <Text
                    as="span"
                    color="primary.500"
                    textStyle={TextStyles.BodyText16Medium}
                  />
                ),
                amountValidated: (
                  <Amount
                    amount={data.amountValidated}
                    currency={data.currency ?? ''}
                  />
                ),
              }}
            />
          )}

        <span>{t('returns:modal.modalDescriptionEnd')}</span>
      </Box>
      <HStack w="full" justifyContent="space-between" mt={5}>
        <Button
          w="full"
          variant="secondary"
          isLoading={cancelMutation.status === 'pending'}
          onClick={handleCancelButtonClick}
        >
          {t('returns:modal.cancelModalButton')}
        </Button>
        <Button
          w="full"
          isLoading={startMutation.status === 'pending'}
          onClick={handleAcceptButtonClick}
        >
          {t('returns:modal.okModalButton')}
        </Button>
      </HStack>
    </VStack>
  );
};

const Amount: FC<{ amount: number; currency: string }> = ({
  amount,
  currency,
}) => {
  const { getCurrencySymbol, getCurrencyUnitRate, getCurrencyDecimalsCount } =
    useCurrencyCallbacks();

  return (
    <Text as="span" color="primary.500" textStyle={TextStyles.BodyText16Medium}>
      <ReactNumberFormat
        suffix={`\u00a0${getCurrencySymbol(currency)}`}
        value={amount / getCurrencyUnitRate(currency)}
        displayType="text"
        isNumericString={true}
        decimalSeparator={'.'}
        decimalScale={getCurrencyDecimalsCount(currency)}
        allowLeadingZeros={true}
        thousandSeparator={' '}
        fixedDecimalScale={true}
      />
    </Text>
  );
};

